import type { ReportKeyValue } from '@reports/domain/types';

/**
 * Value object representing the data for a table block
 *
 * Encapsulates table data with validation and business rules.
 * Immutable by design to ensure data integrity.
 *
 * @example
 * const tableBlockData: TableBlockData = {
 *   columns: [{ key: 'name', value: 'Nombre' }, { key: 'age', value: 'Edad' }],
 *   rows: [['John', '25'], ['Jane', '30']],
 *   pagination: { next: 'api/v1/sales/products/next', prev: 'api/v1/sales/products/prev' },
 * };
 */
export type TableBlockData = Readonly<{
  columns: ReportKeyValue[];
  rows: ReadonlyArray<ReadonlyArray<string>>;
  pagination: {
    readonly next: string | null;
    readonly prev: string | null;
  };
}>;

/** Factory function to create a table block data
 *
 * @param props - The properties to create the table block data
 * @returns {TableBlockData} The created table block data
 *
 * @example
 * const tableBlockData: TableBlockData = createTableBlockData({
 *   columns: [{ key: 'name', value: 'Nombre' }, { key: 'age', value: 'Edad' }],
 *   rows: [['John', '25'], ['Jane', '30']],
 *   pagination: { next: 'api/v1/sales/products/next', prev: 'api/v1/sales/products/prev' },
 * });
 */
export const createTableBlockData = (props: {
  columns: { key: string; value: string }[];
  rows: string[][];
  pagination: {
    next: string | null;
    prev: string | null;
  };
}): TableBlockData => {
  if (!props.columns || props.columns.length === 0) {
    throw new Error('Columns are required');
  }

  if (props.rows && props.rows.length > 0) {
    for (const row of props.rows) {
      if (!row || row.length === 0) {
        throw new Error('Row is required');
      }
    }
  }

  for (const column of props.columns) {
    if (!column.key || !column.value) {
      throw new Error('Column key and value are required');
    }
  }

  return Object.freeze(props);
};
